import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TypingTextContainer, TypingTextWrapper, StaticText, TypingText, Cursor } from './style';

const TypingTextComponent = ({
    originalText = '',
    expandedText = '',
    speed = 150,
    delay = 1000,
    pauseTime = 2000,
    reverseSpeed = 100,
    size = '32px',
    mobileSize = '24px',
    smallSize = '20px',
    color = '#333333',
    typingColor = '#667eea',
    showCursor = true,
    loop = true,
    className,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState('forward');
    const [isTyping, setIsTyping] = useState(false);
    const [isWaiting, setIsWaiting] = useState(false);

    const timerRef = useRef(null);

    // 타이머 정리 함수
    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    // 타이핑 시작 함수
    const startTyping = useCallback(() => {
        clearTimer();
        setIsTyping(true);
        setDirection('forward');
    }, [clearTimer]);

    // 대기 후 역방향 시작 함수
    const startReverse = useCallback(() => {
        clearTimer();
        setIsWaiting(false);
        setIsTyping(true);
        setDirection('backward');
    }, [clearTimer]);

    // 새로운 사이클 시작 함수
    const startNewCycle = useCallback(() => {
        clearTimer();
        setCurrentIndex(0);
        setDirection('forward');
        setIsTyping(true);
    }, [clearTimer]);

    // 메인 타이핑 로직
    useEffect(() => {
        if (!isTyping && !isWaiting && currentIndex === 0) {
            timerRef.current = setTimeout(startTyping, delay);
            return;
        }

        if (isWaiting) {
            return;
        }

        if (!isTyping) {
            return;
        }

        if (direction === 'forward') {
            if (currentIndex < expandedText.length) {
                timerRef.current = setTimeout(() => {
                    setCurrentIndex(currentIndex + 1);
                }, speed);
            } else {
                setIsTyping(false);
                setIsWaiting(true);
                timerRef.current = setTimeout(() => {
                    if (loop) {
                        startReverse();
                    }
                }, pauseTime);
            }
        } else if (direction === 'backward') {
            if (currentIndex > 0) {
                timerRef.current = setTimeout(() => {
                    setCurrentIndex(currentIndex - 1);
                }, reverseSpeed);
            } else {
                setIsTyping(false);
                timerRef.current = setTimeout(() => {
                    if (loop) {
                        startNewCycle();
                    }
                }, delay);
            }
        }
    }, [
        currentIndex,
        direction,
        isTyping,
        isWaiting,
        expandedText,
        speed,
        reverseSpeed,
        delay,
        pauseTime,
        loop,
        startTyping,
        startReverse,
        startNewCycle,
    ]);

    // 컴포넌트 언마운트 시 타이머 정리
    useEffect(() => {
        return () => clearTimer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 원본 텍스트에서 확장된 텍스트로 변환하는 로직
    const renderText = useMemo(() => {
        const parts = [];
        let originalIndex = 0;
        let expandedIndex = 0;
        let displayIndex = 0;

        while (expandedIndex < expandedText.length) {
            if (originalIndex < originalText.length && expandedText[expandedIndex] === originalText[originalIndex]) {
                // 원본 텍스트와 일치하는 부분 - 항상 표시
                parts.push(
                    <StaticText key={`static-${originalIndex}`} color={color}>
                        {expandedText[expandedIndex]}
                    </StaticText>
                );
                originalIndex++;
                displayIndex++;
            } else {
                // 타이핑되는 부분
                const typingStart = expandedIndex;
                let typingEnd = expandedIndex;

                // 연속된 타이핑 문자들을 찾음
                while (
                    typingEnd < expandedText.length &&
                    (originalIndex >= originalText.length || expandedText[typingEnd] !== originalText[originalIndex])
                ) {
                    typingEnd++;
                }

                const typingPart = expandedText.slice(typingStart, typingEnd);
                const typingLength = typingEnd - typingStart;

                // currentIndex를 기준으로 타이핑 부분을 결정
                const shouldShow = displayIndex < currentIndex;
                const showLength = shouldShow ? Math.min(typingLength, currentIndex - displayIndex) : 0;
                const visiblePart = typingPart.slice(0, showLength);

                parts.push(
                    <TypingText
                        key={`typing-${expandedIndex}`}
                        typingColor={typingColor}
                        minWidth={shouldShow ? 'auto' : '0'}
                    >
                        {visiblePart}
                    </TypingText>
                );

                displayIndex += typingLength;
                expandedIndex = typingEnd - 1;
            }
            expandedIndex++;
        }

        return parts;
    }, [expandedText, originalText, currentIndex, color, typingColor]);

    return (
        <TypingTextContainer className={className}>
            <TypingTextWrapper size={size} mobileSize={mobileSize} smallSize={smallSize}>
                {renderText}
                {showCursor && isTyping && <Cursor height={size} />}
            </TypingTextWrapper>
        </TypingTextContainer>
    );
};

export default TypingTextComponent;
